// Admin Children compliance section (rendered inside /admin/compliance under
// the "Children" tab). KPIs over child_health_records by-child + search +
// per-child drawer with admin actions: add, edit, upload, revoke.

import { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldCheck, Warning, FileText, Plus, Trash, FileArrowUp, X, MagnifyingGlass, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  STATUS_META,
  daysUntilExpiry,
  deriveStatus,
  getChildHealthDocumentUrl,
  listChildHealthRecordTypes,
  listChildHealthRecordsForOrg,
  revokeChildHealthRecord,
  rollupByChild,
  suggestExpiryDate,
  typeAppliesToChild,
  upsertChildHealthRecord,
  uploadChildHealthDocument,
} from "@/lib/childHealth";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import {
  listHealthExemptionsForChildren,
  listMedicalProfilesForChildren,
} from "@/lib/healthOperations";
import AdminHealthOperationsPanel from "@/components/health/AdminHealthOperationsPanel";

async function listOrgChildren(organizationId) {
  if (!isSupabaseConfigured || !organizationId) return [];
  const { data, error } = await supabase
    .from("children")
    .select("id, first_name, last_name, date_of_birth, classroom_id, family_id, organization_id, withdrawn_at, classroom:classrooms(id, name, location_id, compliance_program_key)")
    .eq("organization_id", organizationId)
    .order("first_name", { ascending: true });
  if (error) throw error;
  return data || [];
}

const FILTER_OPTIONS = [
  { value: "all",            label: "All children" },
  { value: "expired",        label: "Overdue" },
  { value: "missing",        label: "Missing" },
  { value: "expiring_soon",  label: "Expiring" },
  { value: "configuration",  label: "Configure" },
  { value: "valid",          label: "Compliant" },
];

export default function AdminChildHealthSection({ organizationId }) {
  const [children, setChildren] = useState([]);
  const [types, setTypes] = useState([]);
  const [records, setRecords] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [exemptions, setExemptions] = useState([]);
  const [loading, setLoading] = useState(() => Boolean(organizationId && isSupabaseConfigured));
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [drawer, setDrawer] = useState({ open: false, child: null });
  const [editor, setEditor] = useState({ open: false, child: null, record: null, presetTypeId: null });

  const refresh = useCallback(async () => {
    if (!organizationId || !isSupabaseConfigured) {
      setChildren([]);
      setTypes([]);
      setRecords([]);
      setProfiles([]);
      setExemptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [c, t, r] = await Promise.all([
        listOrgChildren(organizationId),
        listChildHealthRecordTypes(organizationId),
        listChildHealthRecordsForOrg(organizationId),
      ]);
      const activeChildren = (c || []).filter((x) => !x.withdrawn_at);
      const [p, e] = await Promise.all([
        listMedicalProfilesForChildren(activeChildren.map((x) => x.id)),
        listHealthExemptionsForChildren(activeChildren.map((x) => x.id)),
      ]);
      setChildren(activeChildren);
      setTypes(t || []);
      setRecords(r || []);
      setProfiles(p || []);
      setExemptions(e || []);
    } catch (err) {
      toast.error(err.message || "Could not load child health data");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);
  useEffect(() => { refresh(); }, [refresh]);

  const rollups = useMemo(
    () => rollupByChild(records, types, children, exemptions),
    [records, types, children, exemptions],
  );
  const profilesByChild = useMemo(
    () => new Map(profiles.map((profile) => [profile.child_id, profile])),
    [profiles],
  );

  const filtered = useMemo(() => rollups.filter((r) => {
    if (filter !== "all") {
      if (filter === "expired" && r.counts.expired === 0) return false;
      if (filter === "missing" && r.counts.missing === 0) return false;
      if (filter === "expiring_soon" && r.counts.expiring_soon === 0) return false;
      if (filter === "configuration" && r.counts.configuration === 0) return false;
      if (filter === "valid" && (r.counts.expired > 0 || r.counts.missing > 0 || r.counts.expiring_soon > 0 || r.counts.configuration > 0)) return false;
    }
    if (search) {
      const s = search.toLowerCase();
      const name = `${r.child.first_name || ""} ${r.child.last_name || ""}`.toLowerCase();
      if (!name.includes(s)) return false;
    }
    return true;
  }), [rollups, filter, search]);

  const kpis = useMemo(() => ({
    total: rollups.length,
    overdue: rollups.filter((r) => r.counts.expired > 0).length,
    missing: rollups.filter((r) => r.counts.missing > 0).length,
    expiring: rollups.filter((r) => r.counts.expiring_soon > 0).length,
    compliant: rollups.filter((r) => r.counts.expired === 0 && r.counts.missing === 0 && r.counts.expiring_soon === 0 && r.counts.configuration === 0 && (r.records.length > 0 || r.counts.approved_exemptions > 0)).length,
  }), [rollups]);

  const onOpenDrawer = (rollup) => setDrawer({ open: true, child: rollup.child });
  const onAdd = (child, presetTypeId = null) => setEditor({ open: true, child, record: null, presetTypeId });
  const onEdit = (child, record) => setEditor({ open: true, child, record, presetTypeId: null });

  return (
    <div data-testid="admin-child-health" className="space-y-5">
      <div className="flex items-center justify-end">
        <button
          type="button"
          data-testid="admin-child-health-refresh"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
        >
          <ArrowsClockwise size={12} weight="bold" /> Refresh
        </button>
      </div>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi testId="admin-child-health-kpi-total" label="Children tracked" value={kpis.total} />
        <Kpi testId="admin-child-health-kpi-compliant" label="Fully compliant" value={kpis.compliant} tone="ok" />
        <Kpi testId="admin-child-health-kpi-overdue" label="Has overdue" value={kpis.overdue} tone="alert" />
        <Kpi testId="admin-child-health-kpi-missing" label="Has missing" value={kpis.missing} tone="alert" />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          {FILTER_OPTIONS.map((o) => {
            const active = filter === o.value;
            return (
              <button
                key={o.value}
                type="button"
                data-testid={`admin-child-health-filter-${o.value}`}
                onClick={() => setFilter(o.value)}
                aria-pressed={active}
                className={`rounded-full px-3 py-1 text-[11px] font-semibold transition ${
                  active ? "bg-primary text-white" : "border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                }`}
              >
                {o.label}
              </button>
            );
          })}
          <div className="relative ml-auto w-full sm:w-64">
            <MagnifyingGlass size={12} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden />
            <input
              type="text"
              data-testid="admin-child-health-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search children…"
              className="w-full rounded-full border border-neutral-200 bg-white px-9 py-1.5 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
      </section>

      {loading && <p className="text-sm text-neutral-500">Loading…</p>}
      {!loading && filtered.length === 0 && (
        <p data-testid="admin-child-health-empty" className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-6 text-center text-sm text-neutral-500">
          No children match the current filter.
        </p>
      )}

      <ul className="space-y-2" data-testid="admin-child-health-list">
        {filtered.map((r) => (
          <li
            key={r.child.id}
            data-testid={`admin-child-health-row-${r.child.id}`}
            data-worst={r.worst}
            className="flex flex-wrap items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-3"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-neutral-900">{r.child.first_name} {r.child.last_name || ""}</p>
              <p className="truncate text-[11px] text-neutral-500">
                {r.child.classroom?.name || ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5 text-[10px] font-bold uppercase tracking-wider">
              {r.records.length === 0 && r.counts.missing === 0 && (
                <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-neutral-600">No records</span>
              )}
              {r.counts.valid > 0 && <Pill tone="ok">{r.counts.valid} current</Pill>}
              {r.counts.expiring_soon > 0 && <Pill tone="warn">{r.counts.expiring_soon} expiring</Pill>}
              {r.counts.expired > 0 && <Pill tone="alert">{r.counts.expired} overdue</Pill>}
              {r.counts.missing > 0 && <Pill tone="alert">{r.counts.missing} missing</Pill>}
              {r.counts.approved_exemptions > 0 && <Pill tone="ok">{r.counts.approved_exemptions} exempt</Pill>}
              {r.counts.configuration > 0 && <Pill tone="warn">{r.counts.configuration} configure</Pill>}
            </div>
            <button
              type="button"
              data-testid={`admin-child-health-open-${r.child.id}`}
              onClick={() => onOpenDrawer(r)}
              className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"
            >
              Manage
            </button>
          </li>
        ))}
      </ul>

      <ChildDrawer
        open={drawer.open}
        onOpenChange={(o) => setDrawer((s) => ({ ...s, open: o }))}
        child={drawer.child}
        rollup={rollups.find((r) => r.child.id === drawer.child?.id)}
        profile={profilesByChild.get(drawer.child?.id) || null}
        onAdd={(typeId) => onAdd(drawer.child, typeId)}
        onEdit={(rec) => onEdit(drawer.child, rec)}
        onChanged={refresh}
      />

      <RecordEditorSheet
        open={editor.open}
        onOpenChange={(o) => setEditor((s) => ({ ...s, open: o }))}
        child={editor.child}
        record={editor.record}
        presetTypeId={editor.presetTypeId}
        types={types}
        onSaved={refresh}
      />
    </div>
  );
}

function Kpi({ testId, label, value, tone }) {
  const cls = {
    ok:    "border-emerald-200",
    warn:  "border-amber-300",
    alert: "border-[#C6851E]/40",
  }[tone || ""] || "border-neutral-200";
  return (
    <div data-testid={testId} className={`rounded-2xl border bg-white p-4 ${cls}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
      <p className="mt-1 font-heading text-2xl font-semibold text-neutral-900">{value}</p>
    </div>
  );
}

function Pill({ tone, children }) {
  const cls = {
    ok:    "border border-emerald-200 bg-emerald-50 text-emerald-800",
    warn:  "border border-amber-300 bg-amber-50 text-amber-900",
    alert: "border border-[#C6851E]/40 bg-[#C6851E]/10 text-[#8A5A12]",
  }[tone];
  return <span className={`rounded-full px-2 py-0.5 ${cls}`}>{children}</span>;
}

function ChildDrawer({ open, onOpenChange, child, rollup, profile, onAdd, onEdit, onChanged }) {
  if (!child) return null;
  const records = rollup?.records || [];
  const missing = rollup?.missing || [];
  const configuration = rollup?.configuration || [];
  const exemptions = rollup?.exemptions || [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="admin-child-health-drawer"
        side="right"
        className="max-h-[100dvh] w-full overflow-y-auto sm:max-w-lg"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">{child.first_name} {child.last_name || ""}</SheetTitle>
          <SheetDescription>Manage health and immunization records.</SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            Records · {records.length}
          </p>
          <button
            type="button"
            data-testid="admin-child-health-add"
            onClick={() => onAdd(null)}
            className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-white hover:bg-primary-hover"
          >
            <Plus size={11} weight="bold" /> Add record
          </button>
        </div>

        {missing.length > 0 && (
          <section className="mt-3">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[#8A5A12]">Missing required</p>
            <ul className="space-y-2">
              {missing.map((t) => (
                <li
                  key={t.id}
                  data-testid={`admin-child-health-missing-${t.id}`}
                  className="flex items-center gap-3 rounded-2xl border border-[#C6851E]/30 bg-[#C6851E]/5 p-3"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#8A5A12]">
                    <Warning size={16} weight="duotone" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-neutral-900">{t.label}</p>
                    <p className="text-[11px] text-neutral-500">No record on file</p>
                  </div>
                  <button
                    type="button"
                    data-testid={`admin-child-health-missing-add-${t.id}`}
                    onClick={() => onAdd(t.id)}
                    className="rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-white hover:bg-primary-hover"
                  >
                    Add
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        {configuration.length > 0 && (
          <section className="mt-3">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-800">Center configuration needed</p>
            <p className="rounded-2xl border border-amber-300 bg-amber-50 px-3 py-3 text-[13px] text-amber-900">
              {configuration.map((type) => type.label).join(", ")} cannot be evaluated until this child's classroom has the required location or program configuration. This does not block the family.
            </p>
          </section>
        )}

        {records.length === 0 && missing.length === 0 && (
          <p data-testid="admin-child-health-drawer-empty" className="mt-3 rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-4 text-center text-sm text-neutral-500">
            No health records on file.
          </p>
        )}

        {records.length > 0 && (
          <section className="mt-3">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-500">All records</p>
            <ul className="space-y-2">
              {records.map((r) => (
                <RecordRow key={r.id} record={r} onEdit={() => onEdit(r)} onChanged={onChanged} />
              ))}
            </ul>
          </section>
        )}
        <AdminHealthOperationsPanel
          child={child}
          profile={profile}
          exemptions={exemptions}
          onChanged={onChanged}
        />
      </SheetContent>
    </Sheet>
  );
}

function RecordRow({ record, onEdit, onChanged }) {
  const status = deriveStatus(record);
  const meta = STATUS_META[status];
  const days = daysUntilExpiry(record.expires_on);
  const onRevoke = async () => {
    const reason = window.prompt(`Withdraw ${record.record_type?.label}? Optional reason:`, "");
    if (reason === null) return;
    try {
      await revokeChildHealthRecord(record.id, reason);
      toast.success("Record withdrawn");
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "Could not withdraw");
    }
  };
  const onOpenDoc = async () => {
    const url = await getChildHealthDocumentUrl(record.document_path);
    if (url) window.open(url, "_blank", "noopener");
    else toast.error("Document not available");
  };
  return (
    <li
      data-testid={`admin-child-health-record-${record.id}`}
      data-status={status}
      className="rounded-2xl border border-neutral-200 bg-white p-3"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
          <ShieldCheck size={16} weight="duotone" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-900">{record.record_type?.label || "Record"}</p>
          <p className="text-[11px] text-neutral-500">
            Issued {record.issued_on}
            {record.expires_on
              ? ` · expires ${record.expires_on}${days !== null ? ` (${days >= 0 ? `in ${days}d` : `${Math.abs(days)}d ago`})` : ""}`
              : " · no expiry"}
            {record.uploaded_by_role ? ` · by ${record.uploaded_by_role}` : ""}
          </p>
          {record.notes && <p className="mt-0.5 text-[11px] italic text-neutral-500">"{record.notes}"</p>}
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}>
          {meta.label}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <button type="button" data-testid={`admin-child-health-record-${record.id}-edit`} onClick={onEdit}
          className="rounded-full border border-neutral-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50">
          Edit
        </button>
        {record.document_path && (
          <button type="button" data-testid={`admin-child-health-record-${record.id}-doc`} onClick={onOpenDoc}
            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50">
            <FileText size={11} weight="duotone" /> Open
          </button>
        )}
        {!record.revoked_at && (
          <button type="button" data-testid={`admin-child-health-record-${record.id}-revoke`} onClick={onRevoke}
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-[#C6851E]/40 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5">
            <Trash size={11} weight="duotone" /> Withdraw
          </button>
        )}
      </div>
    </li>
  );
}

function RecordEditorSheet({ open, onOpenChange, child, record, presetTypeId, types, onSaved }) {
  const isEdit = !!record;
  const [typeId, setTypeId] = useState("");
  const [issuedOn, setIssuedOn] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const applicable = useMemo(
    () => (child ? types.filter((t) => typeAppliesToChild(t, child)) : []),
    [types, child],
  );

  useEffect(() => {
    if (!open) return;
    setTypeId(record?.record_type_id || presetTypeId || applicable[0]?.id || "");
    setIssuedOn(record?.issued_on || new Date().toISOString().slice(0,10));
    setExpiresOn(record?.expires_on || "");
    setNotes(record?.notes || "");
    setFile(null);
  }, [open, record, presetTypeId, applicable]);

  useEffect(() => {
    if (!open || record) return;
    const t = applicable.find((x) => x.id === typeId);
    if (t?.default_validity_months && issuedOn && !expiresOn) {
      setExpiresOn(suggestExpiryDate(issuedOn, t.default_validity_months));
    }
  }, [typeId, issuedOn, open, record, applicable, expiresOn]);

  const onSave = async () => {
    if (!child?.id) return;
    if (!typeId) { toast.error("Pick a record type"); return; }
    if (!issuedOn) { toast.error("Pick an issue date"); return; }
    setBusy(true);
    try {
      const saved = await upsertChildHealthRecord({
        id: record?.id,
        childId: child.id,
        recordTypeId: typeId,
        issuedOn,
        expiresOn: expiresOn || null,
        documentPath: record?.document_path || null,
        notes,
      });
      if (file && saved?.id) {
        await uploadChildHealthDocument({ recordId: saved.id, file });
      }
      toast.success(isEdit ? "Record updated" : "Record added");
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="admin-child-health-editor"
        side="bottom"
        className="max-h-[92dvh] overflow-y-auto rounded-t-3xl sm:max-w-md sm:mx-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">{isEdit ? "Edit record" : "Add record"}</SheetTitle>
          <SheetDescription>For {child?.first_name} {child?.last_name || ""}.</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          <Field label="Type">
            <select
              data-testid="admin-child-health-editor-type"
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              className={INPUT}
              disabled={isEdit}
            >
              {applicable.map((t) => (<option key={t.id} value={t.id}>{t.label}{t.is_required ? " · required" : ""}</option>))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Issued on">
              <input type="date" data-testid="admin-child-health-editor-issued" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} className={INPUT} />
            </Field>
            <Field label="Expires on (optional)">
              <input type="date" data-testid="admin-child-health-editor-expires" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} className={INPUT} />
            </Field>
          </div>
          <Field label="Notes (optional)">
            <textarea rows={2} data-testid="admin-child-health-editor-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={INPUT} />
          </Field>
          <Field label="Attach proof (optional, PDF / image)">
            <input
              type="file"
              accept="application/pdf,image/*"
              data-testid="admin-child-health-editor-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-neutral-700"
            />
            {record?.document_path && !file && (
              <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-neutral-500">
                <FileArrowUp size={11} weight="duotone" /> Existing doc on file. Picking a new file replaces it after save.
              </p>
            )}
          </Field>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button type="button" data-testid="admin-child-health-editor-cancel" onClick={() => onOpenChange?.(false)} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
            <X size={12} weight="bold" /> Cancel
          </button>
          <button type="button" data-testid="admin-child-health-editor-save" disabled={busy} onClick={onSave} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60">
            <ShieldCheck size={12} weight="duotone" />
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add record"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const INPUT = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</span>
      {children}
    </label>
  );
}
